'use client';

import React from 'react';
import { usePathname } from 'next/navigation';
import Breadcrumb from '@/components/layout/Breadcrumb';
import GlobalNavSidebar from '@/components/layout/GlobalNavSidebar';
import PageBanner from '@/components/layout/PageBanner';

type LayoutType = 'default' | 'sidebar' | 'two-column';

interface DefaultPageProps {
  title: string;
  subtitle?: string;
  headerBackgroundImage?: string;
  headerBackgroundColor?: string;
  children: React.ReactNode;
  showBreadcrumb?: boolean;
  breadcrumbItems?: Array<{ name: string; url: string }>;
  sidebar?: React.ReactNode;
  className?: string;
  layout?: LayoutType;
  leftColumn?: React.ReactNode;
  rightColumn?: React.ReactNode;
  showGlobalSidebar?: boolean; // 是否显示全局右侧栏（非首页时默认显示）
}

/**
 * 默认页面模板
 * 用于常规页面如 About Us, Contact Us 等
 * 
 * 支持的布局类型：
 * - default: 单列居中布局（默认）
 * - sidebar: 70% 主内容 + 30% 侧边栏
 * - two-column: 70% 左列 + 30% 右列（左宽右窄布局，用于非首页页面）
 */
export default function DefaultPage({
  title,
  subtitle,
  headerBackgroundImage,
  headerBackgroundColor,
  children,
  showBreadcrumb = true,
  breadcrumbItems,
  sidebar,
  className = '',
  layout,
  leftColumn,
  rightColumn,
  showGlobalSidebar,
}: DefaultPageProps) {
  const [isClient, setIsClient] = React.useState(false);
  const pathname = usePathname();
  
  // 确保只在客户端执行
  React.useEffect(() => {
    setIsClient(true);
  }, []);

  const isHomePage = isClient && pathname === '/';
  
  // 自动检测是否显示全局右侧栏：非首页且未明确指定 rightColumn 时显示
  const shouldShowGlobalSidebar = showGlobalSidebar !== false && isClient && !isHomePage && !rightColumn;
  
  // 自动检测布局类型
  // 如果需要显示全局右侧栏，强制使用 two-column 布局
  const detectedLayout: LayoutType = shouldShowGlobalSidebar 
    ? 'two-column' 
    : (layout || (sidebar ? 'sidebar' : 'default'));
  return (
    <>
      {/* 页面标题区域 - 已删除，不再显示 */}
      {/* PageHeader 组件已移除，因为用户要求删除header下方、PageBanner上方的标题区域 */}

      {/* 全局页面Banner - 在所有非首页页面显示 */}
      <PageBanner />

      {/* 主要内容区域 - 参考页面结构 */}
      <div id="content" className={`site-content ${className}`}>
        <div className="ast-container">
          {/* 面包屑导航 - 独立 section，白色背景 */}
          {showBreadcrumb && (
            <section className="elementor-section elementor-top-section elementor-element elementor-section-boxed elementor-section-height-default">
              <div className="elementor-container elementor-column-gap-default">
                <div className="elementor-column elementor-col-100 elementor-top-column elementor-element">
                  <div className="elementor-widget-wrap elementor-element-populated">
                    <div className="elementor-element elementor-widget elementor-widget-shortcode">
                      <div className="elementor-widget-container">
                        <Breadcrumb items={breadcrumbItems} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* 内容区域 */}
          <div id="primary" className="content-area primary">
            <main id="main" className="site-main">
              {/* 两列布局 (70% + 30% - 左宽右窄) */}
              {detectedLayout === 'two-column' ? (
                <section className="elementor-section elementor-top-section elementor-element elementor-section-boxed elementor-section-height-default">
                  <div className="elementor-container elementor-column-gap-default">
                    <div className="elementor-column elementor-col-70 elementor-top-column elementor-element">
                      <div className="elementor-widget-wrap elementor-element-populated">
                        {leftColumn || children}
                      </div>
                    </div>
                    <div className="elementor-column elementor-col-30 elementor-top-column elementor-element">
                      <div className="elementor-widget-wrap elementor-element-populated">
                        {shouldShowGlobalSidebar ? <GlobalNavSidebar /> : rightColumn}
                      </div>
                    </div>
                  </div>
                </section>
              ) : detectedLayout === 'sidebar' && sidebar ? (
                // 侧边栏布局 (70% + 30%)
                <section className="elementor-section elementor-top-section elementor-element elementor-section-boxed elementor-section-height-default">
                  <div className="elementor-container elementor-column-gap-default">
                    <div className="elementor-column elementor-col-70 elementor-top-column elementor-element">
                      <div className="elementor-widget-wrap elementor-element-populated">
                        {children}
                      </div>
                    </div>
                    <div className="elementor-column elementor-col-30 elementor-top-column elementor-element">
                      <div className="elementor-widget-wrap elementor-element-populated">
                        {sidebar}
                      </div>
                    </div>
                  </div>
                </section>
              ) : (
                // 默认单列布局
                <section className="elementor-section elementor-top-section elementor-element elementor-section-boxed elementor-section-height-default">
                  <div className="elementor-container elementor-column-gap-default">
                    <div className="elementor-column elementor-col-100 elementor-top-column elementor-element">
                      <div className="elementor-widget-wrap elementor-element-populated">
                        {children}
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </main>
          </div>
        </div>
      </div>
    </>
  );
}

